import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaStar, FaMapMarkerAlt, FaWifi, FaParking, FaSwimmingPool, FaHeart, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { getImageUrl, getPropertyImage, getPropertyImages, getExternalImageProps, FALLBACK_PROPERTY_IMAGE } from '../utils/imageHelper';
import { formatPrice as formatPriceUtil, getPreferredCurrency } from '../utils/currencyPreference';
import { savePropertyActivity } from '../utils/propertyActivities';
import { isInWishlist, toggleWishlist } from '../utils/wishlistAPI';
import { isDiscountVisibleToGuest } from '../utils/offerStatus';
import { SmartMatchBadge } from './SmartMatchBadge';
import { TrustBadgeRow } from './TrustBadge';
import './HotelCard.css';
import './SmartMatchBadge.css';
import './TrustBadge.css';

const EXTERNAL_API_SOURCES = new Set([
  'serpapi',
  'hotelsapi',
  'geoapify',
  'google_places',
  'street_view',
  'makcorps',
  'external',
  'api',
]);

const getOriginalSource = (hotel, source) =>
  String(hotel?.originSource || source || '').toLowerCase();

const isExternalApiHotel = (hotel, source) =>
  EXTERNAL_API_SOURCES.has(getOriginalSource(hotel, source));

const openExternalHotel = (hotel, source) => {
  const originalSource = getOriginalSource(hotel, source);
  if (originalSource === 'serpapi' && hotel?.link) {
    savePropertyActivity(hotel, null);
    window.open(hotel.link, '_blank', 'noopener,noreferrer');
    return true;
  }
  if (originalSource === 'geoapify' || originalSource === 'makcorps') {
    savePropertyActivity(hotel, null);
    const query = encodeURIComponent(`${hotel?.name || 'Hotel'} ${hotel?.city || ''}`.trim());
    window.open(`https://www.google.com/maps/search/${query}`, '_blank', 'noopener,noreferrer');
    return true;
  }
  return false;
};

/**
 * book&stay.com-style Hotel Card Component
 * 
 * Displays hotel information in a professional card format similar to book&stay.com
 * 
 * @param {Object} hotel - Hotel object with all required fields
 * @param {Function} onViewDetails - Callback when button is clicked
 * @param {String} buttonText - Custom button text (default: "See availability")
 */
const HotelCard = ({
  hotel,
  onViewDetails,
  buttonText = 'See availability',
  variant = 'card',
  stayLabel = '',
  urgencyText = '',
  selected = false,
  smartMatch = null,
}) => {
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const {
    id,
    _id,
    name,
    city,
    address,
    images = [],
    imageDetails = null,
    hasRealImages = false,
    rating,
    reviews = 0,
    pricePerNight,
    currency,
    type,
    distanceFromCenter,
    source,
    amenities = [],
    bedrooms,
    bathrooms,
    maxGuests,
    hostName,
    hostEmail,
    ownerName,
    ownerEmail,
    host,
    hostId,
    smartMatch: hotelSmartMatch,
  } = hotel;

  const matchData = smartMatch || hotelSmartMatch;

  const propertyId = _id || id;
  const inventorySource = source === 'api' ? 'api' : 'manual';
  const originalSource = getOriginalSource(hotel, source);
  const displayCurrency = currency || getPreferredCurrency();
  const isLiveInventory = isExternalApiHotel(hotel, source) || inventorySource === 'api';
  const isExternalBooking = isExternalApiHotel(hotel, source);
  const handleImageError = (e, imageList, currentIdx) => {
    const img = e.target;
    for (let i = currentIdx + 1; i < imageList.length; i += 1) {
      const nextUrl = imageList[i];
      if (nextUrl && img.src !== nextUrl) {
        img.src = nextUrl;
        return;
      }
    }
    if (img.src !== FALLBACK_PROPERTY_IMAGE) {
      img.src = FALLBACK_PROPERTY_IMAGE;
    }
  };

  // Check if property is in server wishlist
  useEffect(() => {
    let cancelled = false;
    if (!propertyId) return;
    isInWishlist(propertyId).then((saved) => {
      if (!cancelled) setIsFavorite(saved);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [propertyId]);

  const handleSeeAvailability = () => {
    if (openExternalHotel(hotel, source)) return;
    navigate(`/property/${propertyId}`);
  };

  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    // Optimistic update
    const newState = !isFavorite;
    setIsFavorite(newState);
    window.dispatchEvent(new Event('storage'));
    // Persist to server (or localStorage fallback)
    toggleWishlist({ ...hotel, _id: propertyId, id: propertyId }).then((actual) => {
      setIsFavorite(actual);
      window.dispatchEvent(new Event('storage'));
    }).catch(() => {
      // rollback on error
      setIsFavorite(!newState);
    });
  };

  // Use getPropertyImage helper to ensure proper URL construction
  const primaryImage = getPropertyImage(hotel, 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80');
  const externalImgProps = getExternalImageProps(originalSource, primaryImage);
  
  // Get all images for carousel
  const allImages = getPropertyImages(hotel);
  const hasMultipleImages = allImages.length > 1;

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => prev === 0 ? allImages.length - 1 : prev - 1);
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => prev === allImages.length - 1 ? 0 : prev + 1);
  };

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [propertyId]);

  useEffect(() => {
    if (variant !== 'booking' || !hasMultipleImages) return undefined;
    const autoSlideTimer = window.setInterval(() => {
      setCurrentImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
    }, 4200);

    return () => window.clearInterval(autoSlideTimer);
  }, [variant, hasMultipleImages, allImages.length]);

  // Get image alt text from imageDetails if available
  const getImageAlt = () => {
    if (hasRealImages && imageDetails && imageDetails.length > 0) {
      return imageDetails[0].alt || `${name} - ${type} in ${city}`;
    }
    return `${name} - ${type} in ${city}`;
  };

  // Get image caption from imageDetails if available
  const getImageCaption = () => {
    if (hasRealImages && imageDetails && imageDetails.length > 0) {
      return imageDetails[0].caption;
    }
    return null;
  };

  const formatPrice = () => {
    if (!pricePerNight) return 'Price on request';
    return formatPriceUtil(pricePerNight, displayCurrency);
  };

  const formatDistance = () => {
    if (!distanceFromCenter) return null;
    return `${distanceFromCenter} km from city center`;
  };

  const formatReviews = () => {
    if (!reviews || reviews === 0) return 'No reviews yet';
    if (reviews === 1) return '1 review';
    return `${reviews.toLocaleString()} reviews`;
  };

  const getRatingText = () => {
    if (!rating) return '';
    if (rating >= 9.0) return 'Exceptional';
    if (rating >= 8.0) return 'Very Good';
    if (rating >= 7.0) return 'Good';
    if (rating >= 6.0) return 'Pleasant';
    return 'Fair';
  };

  const postedByName = hostName || ownerName || host?.name || hostId?.name || ((originalSource === 'host' || originalSource === 'internal' || originalSource === 'manual') ? 'Owner' : '');
  const postedByEmail = hostEmail || ownerEmail || host?.email || hostId?.email || 'Email not available';
  const showPostedBy = (originalSource === 'host' || originalSource === 'internal' || originalSource === 'manual') && Boolean(postedByName);

  const getAmenityIcon = (amenity) => {
    const amenityLower = amenity.toLowerCase();
    if (amenityLower.includes('wifi') || amenityLower.includes('internet')) return <FaWifi />;
    if (amenityLower.includes('parking')) return <FaParking />;
    if (amenityLower.includes('pool') || amenityLower.includes('swimming')) return <FaSwimmingPool />;
    return null;
  };

  // List variant: discount %, discounted price
  const discount = hotel.discount;
  const hasDiscount = isDiscountVisibleToGuest(discount);
  let discountedPrice = pricePerNight;
  let discountPercent = 0;
  if (hasDiscount && discount.type === 'percentage') {
    discountPercent = Math.min(100, discount.value || 0);
    discountedPrice = Math.round((pricePerNight || 0) * (1 - discountPercent / 100));
  } else if (hasDiscount && discount.type === 'fixed') {
    discountedPrice = Math.max(0, (pricePerNight || 0) - (discount.value || 0));
    discountPercent = pricePerNight ? Math.round(((pricePerNight - discountedPrice) / pricePerNight) * 100) : 0;
  }

  const cancellationNote =
    hotel.policies?.cancellationPolicy &&
    String(hotel.policies.cancellationPolicy).toLowerCase().includes('free')
      ? 'Free cancellation'
      : null;
  const hasBreakfast =
    amenities.some((a) => String(a).toLowerCase().includes('breakfast')) ||
    (hotel.policies?.paymentPolicy && String(hotel.policies.paymentPolicy).toLowerCase().includes('breakfast'));

  if (variant === 'booking') {
    const scoreDisplay = rating != null ? Number(rating).toFixed(1) : null;
    const roomLine = [
      type || 'Property',
      bedrooms ? `${bedrooms} bed${bedrooms > 1 ? 's' : ''}` : null,
      maxGuests ? `Sleeps ${maxGuests}` : null,
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <article
        className={`hotel-card hotel-card--booking ${selected ? 'hotel-card--booking-active' : ''}`}
        data-source={source}
        onClick={() => {
          if (openExternalHotel(hotel, source)) return;
          onViewDetails && onViewDetails(hotel);
        }}
      >
        <div className="hotel-booking-image">
          <div className="hotel-card-carousel hotel-card-carousel--booking">
            <div
              className="hotel-card-carousel-track"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
            >
              {allImages.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={idx === 0 ? getImageAlt() : `${name} - Photo ${idx + 1}`}
                  className="hotel-card-img hotel-card-img--booking"
                  loading={idx === 0 ? 'eager' : 'lazy'}
                  referrerPolicy={externalImgProps.referrerPolicy}
                  crossOrigin={externalImgProps.crossOrigin}
                  onError={(e) => handleImageError(e, allImages, idx)}
                />
              ))}
            </div>
          </div>
          {hasMultipleImages && (
            <>
              <button className="carousel-arrow carousel-arrow-left" onClick={handlePrevImage} aria-label="Previous">
                <FaChevronLeft />
              </button>
              <button className="carousel-arrow carousel-arrow-right" onClick={handleNextImage} aria-label="Next">
                <FaChevronRight />
              </button>
            </>
          )}
          <button
            type="button"
            className={`hotel-booking-fav ${isFavorite ? 'active' : ''}`}
            onClick={handleToggleFavorite}
            aria-label={isFavorite ? 'Remove from wishlist' : 'Save to wishlist'}
          >
            <FaHeart />
          </button>
          {allImages.length > 0 && (
            <span className="hotel-booking-photo-count">{allImages.length} Photos</span>
          )}
        </div>

        <div className="hotel-booking-main">
          <div className="hotel-booking-top">
            <div className="hotel-booking-score">
              <div className="hotel-booking-score-box">{scoreDisplay ?? '—'}</div>
              <div className="hotel-booking-score-text">
                <span className="hotel-booking-score-label">{getRatingText() || '—'}</span>
                <span className="hotel-booking-score-reviews">{formatReviews()}</span>
              </div>
            </div>
            <div className="hotel-booking-info">
              <h3 className="hotel-booking-name">{name}</h3>
              <p className="hotel-booking-address">
                <FaMapMarkerAlt className="hotel-booking-address-icon" />
                <span>{[address, city].filter(Boolean).join(', ') || formatDistance() || '\u00a0'}</span>
              </p>
              <p className="hotel-booking-roomline">{roomLine}</p>
              {matchData && (
                <div className="hotel-booking-match">
                  <SmartMatchBadge match={matchData} compact showReasons={false} />
                </div>
              )}
              <TrustBadgeRow property={hotel} limit={3} compact className="hotel-booking-trust" />
              {amenities.length > 0 && (
                <div className="hotel-booking-amenities">
                  {amenities.slice(0, 5).map((amenity) => {
                    const icon = getAmenityIcon(amenity);
                    return icon ? (
                      <span key={amenity} className="hotel-booking-amenity-icon" title={amenity}>
                        {icon}
                      </span>
                    ) : null;
                  })}
                </div>
              )}
              <div className="hotel-booking-badges">
                <span
                  className={`hotel-booking-badge hotel-booking-badge--source ${
                    inventorySource === 'api'
                      ? 'hotel-booking-badge--source-api'
                      : 'hotel-booking-badge--source-manual'
                  }`}
                >
                  {inventorySource === 'api' ? '⚡ Live Availability' : '🏨 Partner Stay'}
                </span>
                {cancellationNote && (
                  <span className="hotel-booking-badge hotel-booking-badge--green">{cancellationNote}</span>
                )}
                {hasBreakfast && (
                  <span className="hotel-booking-badge hotel-booking-badge--green">Breakfast included</span>
                )}
                {hasDiscount && (
                  <span className="hotel-booking-badge hotel-booking-badge--green">Limited-time deal</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="hotel-booking-aside">
          <div className="hotel-booking-price">
            {stayLabel && <p className="hotel-booking-stay">{stayLabel}</p>}
            <p
              className={`hotel-booking-was ${
                hasDiscount && pricePerNight > discountedPrice ? 'has-value' : ''
              }`}
            >
              {hasDiscount && pricePerNight > discountedPrice
                ? formatPriceUtil(pricePerNight, displayCurrency)
                : '\u00a0'}
            </p>
            <p className="hotel-booking-amount">
              {formatPriceUtil(discountedPrice || pricePerNight || 0, displayCurrency)}
            </p>
            <p className="hotel-booking-taxes">+ taxes and charges</p>
            <p className={`hotel-booking-urgency ${urgencyText ? 'has-value' : ''}`}>
              {urgencyText || '\u00a0'}
            </p>
          </div>

          <div className="hotel-booking-cta">
            <button
              type="button"
              className="hotel-booking-view-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (onViewDetails) onViewDetails(hotel);
                else handleSeeAvailability();
              }}
            >
              View Details
            </button>
          </div>
        </div>
      </article>
    );
  }

  if (variant === 'list') {
    return (
      <article className="hotel-card hotel-card--list" data-source={source} onClick={() => {
        if (openExternalHotel(hotel, source)) return;
        onViewDetails && onViewDetails(hotel);
      }}>
        <div className="hotel-list-image">
          <div className="hotel-card-carousel">
            {allImages.map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt={idx === 0 ? getImageAlt() : `${name} - Photo ${idx + 1}`}
                className={`hotel-card-img ${idx === currentImageIndex ? 'active' : ''}`}
                loading={idx === 0 ? 'eager' : 'lazy'}
                referrerPolicy={externalImgProps.referrerPolicy}
                crossOrigin={externalImgProps.crossOrigin}                onError={(e) => handleImageError(e, allImages, idx)}
              />
            ))}
          </div>
          {hasMultipleImages && (
            <>
              <button className="carousel-arrow carousel-arrow-left" onClick={handlePrevImage} aria-label="Previous image">
                <FaChevronLeft />
              </button>
              <button className="carousel-arrow carousel-arrow-right" onClick={handleNextImage} aria-label="Next image">
                <FaChevronRight />
              </button>
              <div className="carousel-dots">
                {allImages.slice(0, 5).map((_, idx) => (
                  <span
                    key={idx}
                    className={`carousel-dot ${idx === currentImageIndex ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                  />
                ))}
              </div>
            </>
          )}
          {allImages.length > 1 && (
            <span className="hotel-card-photo-count">📷 {allImages.length}</span>
          )}
        </div>
        <div className="hotel-list-details">
          <h3 className="hotel-list-name">{name}</h3>
          {rating != null && (
            <div className="hotel-list-stars">
              {[1, 2, 3, 4, 5].map((s) => (
                <FaStar key={s} className={s <= Math.round(rating) ? 'star-filled' : 'star-empty'} />
              ))}
            </div>
          )}
          {(address || city) && (
            <div className="hotel-list-location">
              <FaMapMarkerAlt /> {address || city}
            </div>
          )}
          {showPostedBy && (
            <div className="hotel-list-posted-by">
              <span className="hotel-list-posted-by-label">Posted by:</span>
              <span className="hotel-list-posted-by-name">{postedByName}</span>
              <span className="hotel-list-posted-by-email">Email: {postedByEmail}</span>
            </div>
          )}
          {amenities.length > 0 && (
            <div className="hotel-list-amenities">
              {amenities.slice(0, 5).map((a, i) => (
                <span key={i} className="hotel-list-amenity-icon">{getAmenityIcon(a) || <span>•</span>}</span>
              ))}
            </div>
          )}
          {rating != null && (
            <div className="hotel-list-rating-pill">
              {rating.toFixed(1)} {getRatingText()} ({reviews ? reviews.toLocaleString() : 0} reviews)
            </div>
          )}
          <div className="hotel-list-last-booked">Last booked – recently</div>
        </div>
        <div className="hotel-list-price-block">
          {hasDiscount && discountPercent > 0 && (
            <span className="hotel-list-discount-badge">{discountPercent}% Off!</span>
          )}
          {hasDiscount && pricePerNight > discountedPrice && (
            <div className="hotel-list-original-price">
              {formatPriceUtil(pricePerNight, displayCurrency)}
            </div>
          )}
          <div className="hotel-list-price">
            {formatPriceUtil(discountedPrice || pricePerNight || 0, displayCurrency)}
          </div>
          <div className="hotel-list-price-note">1 Room/Night</div>
          <button
            type="button"
            className="hotel-list-book-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (openExternalHotel(hotel, source)) return;
              onViewDetails && onViewDetails(hotel);
            }}
          >
            {isExternalBooking ? 'Book Now ↗' : 'Book Now'}
          </button>
        </div>
      </article>
    );
  }

  return (
    <article className="hotel-card" data-source={source}>
      <div className="hotel-card-image-wrapper">
        <div className="hotel-card-image">
          <div className="hotel-card-carousel">
            {allImages.map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt={idx === 0 ? getImageAlt() : `${name} - Photo ${idx + 1}`}
                className={`hotel-card-img ${idx === currentImageIndex ? 'active' : ''}`}
                loading={idx === 0 ? 'eager' : 'lazy'}
                referrerPolicy={externalImgProps.referrerPolicy}
                crossOrigin={externalImgProps.crossOrigin}                onError={(e) => handleImageError(e, allImages, idx)}
              />
            ))}
          </div>
          {hasMultipleImages && (
            <>
              <button className="carousel-arrow carousel-arrow-left" onClick={handlePrevImage} aria-label="Previous image">
                <FaChevronLeft />
              </button>
              <button className="carousel-arrow carousel-arrow-right" onClick={handleNextImage} aria-label="Next image">
                <FaChevronRight />
              </button>
              <div className="carousel-dots">
                {allImages.slice(0, 5).map((_, idx) => (
                  <span
                    key={idx}
                    className={`carousel-dot ${idx === currentImageIndex ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                  />
                ))}
                {allImages.length > 5 && <span className="carousel-dot-more">+{allImages.length - 5}</span>}
              </div>
            </>
          )}
          {allImages.length > 1 && (
            <span className="hotel-card-photo-count">
              📷 {allImages.length}
            </span>
          )}
          {hasRealImages && (
            <div className="hotel-card-image-badge">
              <span className="real-image-badge">📸 Real Photo</span>
            </div>
          )}
          {getImageCaption() && (
            <div className="hotel-card-image-caption">
              {getImageCaption()}
            </div>
          )}
          <button 
            className={`hotel-card-favorite ${isFavorite ? 'active' : ''}`}
            onClick={handleToggleFavorite}
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <FaHeart />
          </button>
          {originalSource === 'host' && (
            <span className="hotel-card-badge hotel-card-badge-host">
              🏠 Host Property
            </span>
          )}
          {originalSource === 'external' && (
            <span className="hotel-card-badge hotel-card-badge-external">
              External
            </span>
          )}
        </div>
      </div>

      <div className="hotel-card-content">
        <div className="hotel-card-header">
          <div className="hotel-card-title-section">
            <h3 className="hotel-card-name">{name}</h3>
            {type && (
              <span className="hotel-card-type">{type}</span>
            )}
            {distanceFromCenter && (
              <div className="hotel-card-distance">
                <FaMapMarkerAlt className="hotel-card-location-icon" />
                <span>{formatDistance()}</span>
              </div>
            )}
          </div>
          
          <div className="hotel-card-rating-section">
            {rating && (
              <>
                <div className="hotel-card-rating-text">
                  <span className="hotel-card-rating-label">{getRatingText()}</span>
                  <span className="hotel-card-review-count">{formatReviews()}</span>
                </div>
                <div className="hotel-card-rating-score">
                  {rating.toFixed(1)}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="hotel-card-details">
          {address && (
            <div className="hotel-card-address">
              {address}
            </div>
          )}
          {showPostedBy && (
            <div className="hotel-card-posted-by">
              <span>Posted by: <strong>{postedByName}</strong></span>
              <span className="hotel-card-posted-by-email">Email: {postedByEmail}</span>
            </div>
          )}
          
          {/* Show property details for host properties */}
          {originalSource === 'host' && (bedrooms || bathrooms || maxGuests) && (
            <div className="hotel-card-property-details">
              {bedrooms > 0 && <span>🛏️ {bedrooms} Bedroom{bedrooms > 1 ? 's' : ''}</span>}
              {bathrooms > 0 && <span>🚿 {bathrooms} Bathroom{bathrooms > 1 ? 's' : ''}</span>}
              {maxGuests > 0 && <span>👥 Up to {maxGuests} guests</span>}
            </div>
          )}
          
          {amenities.length > 0 && (
            <div className="hotel-card-amenities">
              {(showAllAmenities ? amenities : amenities.slice(0, 3)).map((amenity, index) => (
                <div key={index} className="hotel-card-amenity">
                  {getAmenityIcon(amenity)}
                  <span>{amenity}</span>
                </div>
              ))}
              {amenities.length > 3 && (
                <button
                  className="hotel-card-view-more-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAllAmenities(!showAllAmenities);
                  }}
                  type="button"
                >
                  {showAllAmenities ? 'View Less' : `+${amenities.length - 3} more`}
                </button>
              )}
            </div>
          )}
          <TrustBadgeRow property={hotel} limit={3} compact />
        </div>

        <div className="hotel-card-footer">
          <div className="hotel-card-price-section">
            <div className="hotel-card-price-label">1 night, 2 adults</div>
            <div className="hotel-card-price">{formatPrice()}</div>
            <div className="hotel-card-price-note">+{formatPriceUtil(Math.round((pricePerNight || 0) * 0.12), displayCurrency)} taxes and charges</div>
          </div>
          
          <div className="hotel-card-actions">
            <div className="hotel-card-availability">
              <span className="availability-status">✓ Available</span>
              <span className="availability-note">{isExternalBooking ? 'External booking' : 'Free cancellation'}</span>
            </div>
            <button 
              className="hotel-card-button"
              onClick={handleSeeAvailability}
            >
              {isExternalBooking ? 'Book Now ↗' : buttonText}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};

export default HotelCard;

